import { MapPin, Eye, CheckCircle, RotateCcw, Clock, X } from 'lucide-react';
import { useState } from 'react';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || '';

// Helper function to normalize image URLs
const normalizeImageUrl = (url) => {
  if (!url) return null;
  
  // Handle new image object format
  if (typeof url === 'object' && url.url) {
    return url.url;
  }
  
  // Handle string URLs
  if (typeof url !== 'string') return null;
  
  // If it's already a full external URL, return as is
  if (url.startsWith('http')) return url;
  
  // If it's a relative URL starting with /api/, prepend the API base
  if (url.startsWith('/api/')) {
    return `${API_BASE_URL}${url}`;
  }
  
  return url;
};

const CATEGORY_IMAGES = {
  electronics: 'https://images.unsplash.com/photo-1750797308931-b0d261abb3d5?w=400&h=400&fit=crop',
  furniture: 'https://images.unsplash.com/photo-1673695008224-fa5a9d195dfb?w=400&h=400&fit=crop',
  clothing: 'https://images.unsplash.com/photo-1489987707025-afc232f7ea0f?w=400&h=400&fit=crop',
  vehicles: 'https://images.unsplash.com/photo-1549317661-bd32c8ce0db2?w=400&h=400&fit=crop',
  sports: 'https://images.unsplash.com/photo-1461896836934-fffbd4f9b0db?w=400&h=400&fit=crop',
  home: 'https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?w=400&h=400&fit=crop',
  collectibles: 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=400&h=400&fit=crop',
  books: 'https://images.unsplash.com/photo-1512820790803-83ca734da794?w=400&h=400&fit=crop',
  tools: 'https://images.unsplash.com/photo-1416879595882-3373a0480b5b?w=400&h=400&fit=crop',
  other: 'https://images.unsplash.com/photo-1586864387967-d02ef85d93e8?w=400&h=400&fit=crop',
};

export const WTBCard = ({ 
  listing, 
  onClick, 
  showActions = false, 
  onClose, 
  onReopen, 
  variant = 'standard' 
}) => {
  const [imgError, setImgError] = useState(false);
  const [isExtremeAspect, setIsExtremeAspect] = useState(false);
  
  const isClosed = listing.status === 'closed';
  
  // Get the image URL
  const getImageUrl = () => {
    if (imgError) {
      return CATEGORY_IMAGES[listing.category] || CATEGORY_IMAGES.other;
    }
    const imgUrl = listing.images?.[0];
    const normalized = normalizeImageUrl(imgUrl);
    return normalized || CATEGORY_IMAGES[listing.category] || CATEGORY_IMAGES.other;
  };
  
  const imageUrl = getImageUrl();
  const viewCount = listing.views || 0;
  
  // Format price range
  const formatPrice = () => {
    const min = listing.min_price || listing.price_min || 0;
    const max = listing.max_price || listing.price_max || 0;
    if (min && max && min !== max) {
      return `$${min.toLocaleString()} - $${max.toLocaleString()}`;
    }
    if (max) {
      return `Up to $${max.toLocaleString()}`;
    }
    if (min) {
      return `From $${min.toLocaleString()}`;
    }
    return 'Price negotiable';
  };

  // Get flexibility text
  const getFlexibilityText = () => {
    const flex = listing.flexibility || listing.price_flexibility || 3;
    return flex <= 2 ? 'Firm' : 'Flexible';
  };

  // Detect aspect ratio on image load
  const handleImageLoad = (e) => {
    const img = e.target;
    const aspectRatio = img.naturalWidth / img.naturalHeight;
    setIsExtremeAspect(aspectRatio > 2 || aspectRatio < 0.5);
  };

  // List view variant
  if (variant === 'list') {
    return (
      <div 
        className={`flex cursor-pointer transition-all duration-200 hover:shadow-lg bg-[var(--theme-card)] rounded-xl border border-[var(--theme-border)] overflow-hidden ${isClosed ? 'opacity-70' : ''}`}
        onClick={onClick}
        data-testid={`wtb-card-${listing.id}`}
      >
        {/* Image - fixed width */}
        <div className="relative w-32 h-32 flex-shrink-0 overflow-hidden">
          <img 
            src={imageUrl}
            alt={listing.title}
            className={`w-full h-full object-cover ${isClosed ? 'grayscale-[30%]' : ''}`}
            onError={() => setImgError(true)}
          />
          {/* Status Badge */}
          <div className="absolute top-2 left-2 z-20">
            <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide ${
              isClosed
                ? 'bg-[#6B7280]/90 text-white'
                : 'bg-[#1E3A5F]/90 text-white'
            }`}>
              {isClosed ? 'CLOSED' : 'WANTED'}
            </span>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 p-3 flex flex-col justify-between min-w-0">
          <div>
            <h3 className="font-semibold text-[var(--theme-text-primary)] truncate">{listing.title}</h3>
            <p className="text-lg font-bold text-[#059669] mt-1">{formatPrice()}</p>
            <div className="flex items-center gap-2 mt-1 text-xs text-[var(--theme-text-muted)]">
              <MapPin className="w-3 h-3" />
              <span className="truncate">{listing.location_name || listing.location || 'Any location'}</span>
              <span className="mx-1">•</span>
              <Eye className="w-3 h-3" />
              <span>{viewCount}</span>
            </div>
          </div>

          {/* Action Buttons */}
          {showActions && (onClose || onReopen) && (
            <div className="flex gap-2 mt-2">
              {onClose && !isClosed && (
                <button
                  onClick={(e) => { e.stopPropagation(); onClose(); }}
                  className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-[#6B7280] bg-[#6B7280]/10 hover:bg-[#6B7280]/20 rounded-lg transition-colors"
                  data-testid={`close-want-${listing.id}`}
                >
                  <X className="w-3 h-3" />
                  Close
                </button>
              )}
              {onReopen && isClosed && (
                <button
                  onClick={(e) => { e.stopPropagation(); onReopen(); }}
                  className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-[#C9956C] bg-[#C9956C]/10 hover:bg-[#C9956C]/20 rounded-lg transition-colors"
                  data-testid={`reopen-want-${listing.id}`}
                >
                  <RotateCcw className="w-3 h-3" />
                  Reopen
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    );
  }

  // Standard/Large card view (matching InventoryCard layout)
  return (
    <div 
      className={`bg-[var(--theme-card)] rounded-xl overflow-hidden border border-[var(--theme-border)] hover:border-[var(--theme-border-hover)] transition-all duration-300 group cursor-pointer ${isClosed ? 'opacity-70' : ''}`}
      onClick={onClick}
      data-testid={`wtb-card-${listing.id}`}
    >
      {/* Image - Square aspect ratio like InventoryCard */}
      <div className="relative aspect-square overflow-hidden bg-[var(--theme-hover)]">
        {/* Blur background for extreme aspect ratios */}
        {isExtremeAspect && (
          <div 
            className="absolute inset-0 bg-cover bg-center blur-xl scale-110 opacity-40"
            style={{ backgroundImage: `url(${imageUrl})` }}
          />
        )}
        
        <img 
          src={imageUrl} 
          alt={listing.title}
          onLoad={handleImageLoad}
          onError={() => setImgError(true)}
          className={`w-full h-full transition-transform duration-500 group-hover:scale-105 relative z-10 ${
            isExtremeAspect ? 'object-contain' : 'object-cover'
          } ${isClosed ? 'grayscale-[30%]' : ''}`}
        />
        
        {/* Status Badge */}
        <div className="absolute top-2 left-2 z-20">
          <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide ${
            isClosed
              ? 'bg-[#6B7280]/90 text-white'
              : 'bg-[#1E3A5F]/90 text-white'
          }`}>
            {isClosed ? 'CLOSED' : 'WANTED'}
          </span>
        </div>

        {/* Views Badge - bottom right like InventoryCard */}
        <div className="absolute bottom-2 right-2 z-20 flex items-center gap-1 px-2 py-0.5 rounded-full bg-black/60 backdrop-blur-sm text-white text-[10px]">
          <Eye className="w-3 h-3" />
          <span>{viewCount}</span>
        </div>
      </div>

      {/* Content - Matching InventoryCard layout */}
      <div className="p-2.5">
        {/* Title - 2 lines */}
        <h3 className="font-semibold text-sm text-[var(--theme-text-primary)] line-clamp-2 max-h-[2.2rem] overflow-hidden group-hover:text-[#1E3A5F] transition-colors leading-tight">
          {listing.title}
        </h3>
        
        {/* Price - Green, below title */}
        <div className="flex items-center gap-2 mt-0.5">
          <span className="text-base font-bold text-[#059669]">{formatPrice()}</span>
          <span className="text-[10px] font-medium text-[var(--theme-text-muted)] uppercase">
            {getFlexibilityText()}
          </span>
        </div>

        {/* Footer row: Location */}
        <div className="flex items-center justify-between mt-2 text-[11px] text-[var(--theme-text-muted)]">
          <div className="flex items-center gap-1.5 flex-1 min-w-0">
            <MapPin className="w-3 h-3 flex-shrink-0 text-[var(--theme-text-muted)]" />
            <span className="truncate">{listing.location_name || listing.location || 'Any location'}</span>
          </div>
          
          {/* Search radius if available */}
          {listing.search_radius_km && (
            <span className="flex-shrink-0 ml-2 text-[var(--theme-text-muted)]">
              {listing.search_radius_km}km
            </span>
          )}
        </div>

        {/* Accepted conditions pills */}
        {listing.condition_accepted && listing.condition_accepted.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-2">
            {listing.condition_accepted.slice(0, 3).map((cond) => (
              <span 
                key={cond} 
                className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-[#F5F0E8] text-[9px] text-[var(--theme-text-secondary)] capitalize"
              >
                <CheckCircle className="w-2.5 h-2.5 text-[#059669]" />
                {cond.replace('_', ' ')}
              </span>
            ))}
            {listing.condition_accepted.length > 3 && (
              <span className="text-[9px] text-[var(--theme-text-muted)]">
                +{listing.condition_accepted.length - 3}
              </span>
            )}
          </div>
        )}

        {/* Action Buttons */}
        {showActions && (onClose || onReopen) && (
          <div className="mt-2 pt-2 border-t border-[var(--theme-border)]">
            {onClose && !isClosed && (
              <button
                onClick={(e) => { e.stopPropagation(); onClose(); }}
                className="w-full flex items-center justify-center gap-1.5 px-2 py-1.5 text-xs font-medium text-[#6B7280] bg-[#6B7280]/10 hover:bg-[#6B7280]/20 rounded-lg transition-colors"
                data-testid={`close-want-${listing.id}`}
              >
                <X className="w-3.5 h-3.5" />
                Close Want
              </button>
            )}
            {onReopen && isClosed && (
              <button
                onClick={(e) => { e.stopPropagation(); onReopen(); }}
                className="w-full flex items-center justify-center gap-1.5 px-2 py-1.5 text-xs font-medium text-[#C9956C] bg-[#C9956C]/10 hover:bg-[#C9956C]/20 rounded-lg transition-colors"
                data-testid={`reopen-want-${listing.id}`}
              >
                <RotateCcw className="w-3.5 h-3.5" />
                Reopen Want
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default WTBCard;
